<script setup lang="ts">
import LayoutContainer from "~/layouts/partials/LayoutContainer.vue"
import AdminDrawerContainer from "~/layouts/partials/AdminDrawerContainer.vue"
import AdminMenu from "~/layouts/partials/AdminMenu.vue"
import ProfileMenu from "~/components/ProfileMenu.vue"
import ApplicationLogo from "~/components/ApplicationLogo.vue"
import { useModal } from "~/composables/useModal"
import { onMounted } from "vue"
import { useMenuItemSelectedStore } from "~/stores/MenuItemSelected"
import SelectStore from "~/components/SelectStore.vue"
import { goToRoute } from "~/common/helpers"
import { useFilteredMenu } from "~/composables/LefMenu"
import { adminMenu } from "~/common/main_menu"
import type { User } from "~/models/User"

/**
 *
 *  Props and emits section
 *
 *
 */

/**
 *
 *  Ref, computed and hooks section
 *
 *
 */

const router = useRouter()
const menuItemSelectedStore = useMenuItemSelectedStore()
const initSettingsStore = useDefaultSettingsStore()
const { get } = useOFetchCustom("init-settings")
const { modal: collapseMenu, toggleModal: toggleCollapseMenu } = useModal()
const {
    modal: mobileDrawerOpen,
    toggleModal: toggleMobileDrawer,
    closeModal: closeMobileDrawer,
} = useModal(false)
const windowWidth = computed(() => window.innerWidth)
const mainMenu = useFilteredMenu(adminMenu)
const user = useSanctumUser<User>()
const { logout: lout } = useSanctumAuth()
const shopCartStore = useShopCartStore()
const currentStoreStore = useCurrentStoreStore()
const profileImage = computed(() => user.value?.profile_photo_url)
const profileImageError = ref(false)
watch(profileImage, () => {
    profileImageError.value = false
})

/**
 *
 *  Life cicle events
 *
 *
 */

watch(
    () => router.currentRoute.value.path,
    () => {
        closeMobileDrawer()
    }
)

watch(mobileDrawerOpen, (open) => {
    document.body.style.overflow = open ? "hidden" : ""
})

onMounted(async () => {
    get(undefined, {
        onSuccess: (res) => {
            initSettingsStore.setSettings(res.data)
        },
    })

    const route_name = router.currentRoute.value.name

    if (route_name) {
        menuItemSelectedStore.setByRoute(route_name.toString())
    }
})

/**
 *
 *  Functions section
 *
 *
 */

async function logout() {
    lout().then(() => {
        shopCartStore.clearCart()
        currentStoreStore.clearCurrentStore()
        navigateTo("/", {})
        refreshNuxtData()
    })
}
</script>

<template>
    <LayoutContainer>
        <div class="tw-col-span-12 tw-flex tw-bg-adminAccent">
            <!-- Desktop sidebar drawer -->
            <div v-if="windowWidth >= 1024">
                <AdminDrawerContainer v-if="!collapseMenu" />
            </div>

            <!-- Mobile overlay drawer -->
            <Transition name="mobile-drawer">
                <div
                    v-if="mobileDrawerOpen"
                    class="lg:tw-hidden tw-fixed tw-inset-0 tw-z-[200] tw-flex"
                >
                    <div
                        class="tw-absolute tw-inset-0 tw-bg-black/60"
                        @click="closeMobileDrawer"
                    />
                    <aside
                        class="admin-mobile-drawer tw-relative tw-w-[280px] tw-bg-adminPrimary-500 tw-overflow-y-auto tw-overflow-x-hidden tw-flex tw-flex-col tw-py-5 tw-gap-4"
                    >
                        <div class="tw-flex tw-items-center tw-gap-2 tw-px-2">
                            <q-avatar size="30px">
                                <img
                                    v-if="profileImage && !profileImageError"
                                    :src="profileImage"
                                    alt="profile"
                                    class="tw-rounded-full tw-object-cover"
                                    @error="profileImageError = true"
                                />
                                <q-icon
                                    v-else
                                    name="person"
                                    size="24px"
                                    class="tw-text-adminAccent-200"
                                />
                            </q-avatar>
                            <span
                                class="tw-text-adminAccent-300 tw-flex-1 tw-truncate"
                                >{{ user?.name }}</span
                            >
                            <q-btn
                                flat
                                round
                                dense
                                icon="sym_o_logout"
                                class="tw-text-red-400"
                                @click="logout"
                            />
                        </div>
                        <AdminMenu :menu="mainMenu" />
                    </aside>
                </div>
            </Transition>

            <div class="tw-flex tw-flex-col tw-w-full">
                <nav
                    class="tw-sticky tw-top-0 tw-z-50 lg:tw-relative tw-flex tw-flex-col lg:tw-flex-row lg:tw-items-center lg:tw-justify-between lg:tw-gap-4 lg:tw-px-5 lg:tw-py-3 tw-bg-adminPrimary-500"
                >
                    <div
                        class="tw-w-full tw-flex tw-flex-col lg:tw-flex-row lg:tw-items-center tw-gap-4 tw-py-2 lg:tw-py-0"
                    >
                        <div
                            class="tw-flex tw-items-center tw-gap-4 tw-px-2 lg:tw-px-0"
                        >
                            <q-icon
                                :name="
                                    collapseMenu
                                        ? 'sym_o_left_panel_open'
                                        : 'sym_o_left_panel_close'
                                "
                                size="20px"
                                @click="toggleCollapseMenu"
                                class="tw-cursor-pointer tw-text-adminAccent-200 tw-hidden lg:tw-block"
                            />
                            <q-icon
                                name="sym_o_menu"
                                size="20px"
                                @click="toggleMobileDrawer"
                                class="tw-cursor-pointer tw-text-adminAccent-200 lg:tw-hidden"
                            />
                            <div @click="goToRoute('/')">
                                <ApplicationLogo class="tw-h-[20px]" />
                            </div>
                            <div
                                class="lg:tw-hidden tw-flex tw-items-center tw-gap-2 tw-ml-auto"
                            >
                                <SelectStore />
                            </div>
                        </div>
                        <div
                            class="tw-hidden lg:tw-block tw-w-[1px] tw-h-[20px] tw-bg-adminAccent-200"
                        ></div>
                        <div
                            class="tw-flex tw-items-center tw-gap-2 tw-overflow-x-auto lg:tw-overflow-hidden tw-w-full tw-px-2"
                        >
                            <slot name="breadCrumbs">
                                <BreadCrumbs />
                            </slot>
                        </div>
                    </div>
                    <div
                        class="tw-hidden lg:tw-flex lg:tw-items-center lg:tw-gap-2"
                    >
                        <SelectStore />
                        <ProfileMenu showAdminStyle />
                    </div>
                </nav>
                <main
                    class="tw-m-1 lg:tw-m-5 tw-z-10 tw-flex-1 tw-grid tw-grid-cols-1"
                >
                    <slot></slot>
                </main>
            </div>
        </div>
    </LayoutContainer>
</template>

<style>
.shadown {
    box-shadow: 0px 4px 4px 0px rgba(6, 116, 191, 0.15);
}
.body-blur {
    filter: blur(3px);
}
.admin-mobile-drawer {
    scrollbar-width: none;
}
.admin-mobile-drawer::-webkit-scrollbar {
    display: none;
}
.mobile-drawer-enter-active,
.mobile-drawer-leave-active {
    transition: opacity 0.25s ease;
}
.mobile-drawer-enter-active .admin-mobile-drawer,
.mobile-drawer-leave-active .admin-mobile-drawer {
    transition: transform 0.25s ease;
}
.mobile-drawer-enter-from,
.mobile-drawer-leave-to {
    opacity: 0;
}
.mobile-drawer-enter-from .admin-mobile-drawer,
.mobile-drawer-leave-to .admin-mobile-drawer {
    transform: translateX(-100%);
}
</style>
